import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId, scrollToFormField } from "@/lib/scroll";

const NAV = [
  { id: "inicio", label: "Inicio" },
  { id: "tamanos", label: "Tamaños y precios" },
  { id: "ventajas", label: "Ventajas" },
  { id: "plano", label: "Plano" },
  { id: "ubicacion", label: "Ubicación" },
  { id: "faq", label: "Preguntas frecuentes" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  const goForm = () => {
    setOpen(false);
    scrollToFormField();
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-[background-color,box-shadow,border-color] duration-300 border-b ${
        scrolled || open
          ? "bg-white/90 backdrop-blur-xl border-brand-ink/10 shadow-[0_8px_30px_rgba(18,19,22,0.06)]"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button onClick={() => go("inicio")} aria-label="Ir al inicio" className="shrink-0">
          <Logo dark={!scrolled && !open} />
        </button>

        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
          {NAV.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-link-${item.id}`}
              onClick={() => go(item.id)}
              className={`text-sm font-semibold transition-colors duration-200 hover:text-brand-ink ${
                scrolled ? "text-brand-ink/70" : "text-white/85 hover:text-white"
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <motion.button
            data-testid="nav-contact-button"
            onClick={goForm}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            className="hidden sm:inline-flex items-center bg-brand-yellow text-brand-ink font-display font-extrabold text-sm tracking-wide px-6 py-3 rounded-full shadow-[0_6px_20px_rgba(255,230,0,0.35)] hover:bg-brand-yellowhover transition-colors duration-200"
          >
            CONTACTAR
          </motion.button>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            aria-label="Abrir menú"
            className={`lg:hidden p-2 rounded-lg ${scrolled || open ? "text-brand-ink" : "text-white"}`}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-brand-ink/10"
          >
            <div className="px-5 py-4 flex flex-col">
              {NAV.map((item) => (
                <button
                  key={item.id}
                  data-testid={`mobile-nav-link-${item.id}`}
                  onClick={() => go(item.id)}
                  className="text-left py-3.5 text-base font-semibold text-brand-ink border-b border-brand-ink/5"
                >
                  {item.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-contact-button"
                onClick={goForm}
                className="mt-4 bg-brand-yellow text-brand-ink font-display font-extrabold tracking-wide px-6 py-4 rounded-full"
              >
                CONTACTAR
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
